// src/data/changes2026.ts
//
// 2026년 확정된 정부지원금 핵심 기준 수치
// 출처: 보건복지부 「2026년 기준 중위소득 및 급여별 선정기준」 고시,
//       고용노동부 「2026년 적용 최저임금」 고시(2025.8.5),
//       보건복지부 「2026년 기초연금 선정기준액」, 한국장학재단, 한국에너지공단
// 최종 확인일: 2026-09-02
//
import { CategoryId } from '../types';

/** 2026년 가구원수별 기준 중위소득 (월, 원) */
export const medianIncome2026 = [
    { household: '1인 가구', amount: 2564238 },
    { household: '2인 가구', amount: 4199292 },
    { household: '3인 가구', amount: 5359036 },
    { household: '4인 가구', amount: 6494738 },
    { household: '5인 가구', amount: 7556719 },
    { household: '6인 가구', amount: 8555952 },
];

/** 급여별 선정기준 (기준 중위소득 대비 비율) */
export const benefitThresholds2026 = [
    { name: '생계급여', ratio: 32, fourPerson: 2078316, onePerson: 820556 },
    { name: '의료급여', ratio: 40, fourPerson: 2597895, onePerson: 1025695 },
    { name: '주거급여', ratio: 48, fourPerson: 3117474, onePerson: 1230834 },
    { name: '교육급여', ratio: 50, fourPerson: 3247369, onePerson: 1282119 },
];

/** 홈 상단에 노출하는 2026년 핵심 지표 */
export const keyFigures2026 = [
    {
        label: '2026년 최저임금',
        value: '10,320원',
        sub: '월 환산 2,156,880원 (209시간)',
        change: '전년 대비 +2.9%',
    },
    {
        label: '기준 중위소득 (4인)',
        value: '6,494,738원',
        sub: '생계급여 4인 2,078,316원',
        change: '전년 대비 +6.51%',
    },
    {
        label: '기초연금 최대 지급액',
        value: '월 349,700원',
        sub: '선정기준 단독 247만원',
        change: '수급자 약 43만명 증가',
    },
    {
        label: '국가장학금 지원 규모',
        value: '5조 1,161억원',
        sub: '9구간까지 확대, 약 150만명',
        change: '역대 최대 규모',
    },
];

export interface Change2026 {
    id: string;
    category: CategoryId;
    title: string;
    before: string;
    after: string;
    detail: string;
    slug?: string;
}

/** 2026년에 실제로 달라진 제도 */
export const changes2026: Change2026[] = [
    {
        id: 'median-income',
        category: 'welfare',
        title: '기준 중위소득 6.51% 인상',
        before: '4인 가구 6,097,773원',
        after: '4인 가구 6,494,738원',
        detail:
            '기준 중위소득은 생계·의료·주거·교육급여를 포함한 70여 개 복지사업의 선정 기준입니다. 6.51% 인상되면서 모든 급여의 선정기준선과 지급액이 함께 올랐습니다.',
        slug: 'saenggye-geubyeo',
    },
    {
        id: 'minimum-wage',
        category: 'worker',
        title: '최저임금 시간급 10,320원',
        before: '시간급 10,030원',
        after: '시간급 10,320원 (월 2,156,880원)',
        detail:
            '17년 만에 노사 합의로 결정된 최저임금입니다. 업종 구분 없이 모든 사업장에 동일하게 적용되며, 실업급여 하한액 등 다른 제도의 기준으로도 쓰입니다.',
        slug: 'silup-geubyeo',
    },
    {
        id: 'saenggye-up',
        category: 'welfare',
        title: '생계급여 4인 가구 월 207만원',
        before: '월 1,951,287원',
        after: '월 2,078,316원',
        detail: '4인 가구 기준으로 월 약 12만 7천원이 인상되었습니다. 선정기준이 곧 최저보장수준입니다.',
        slug: 'saenggye-geubyeo',
    },
    {
        id: 'gicho-yeongeum-up',
        category: 'welfare',
        title: '기초연금 선정기준액 대폭 인상',
        before: '단독가구 228만원 / 부부 364만 8천원',
        after: '단독가구 247만원 / 부부 395만 2천원',
        detail:
            '선정기준액이 크게 오르면서 수급 대상자가 736만명에서 779만명으로 약 43만명 늘어날 것으로 예상됩니다. 지급액은 물가상승률 2.1%를 반영해 단독가구 월 최대 349,700원입니다.',
        slug: 'gicho-yeongeum',
    },
    {
        id: 'janghakgeum-9',
        category: 'education',
        title: '국가장학금 9구간까지 확대',
        before: '8구간까지 지원',
        after: '9구간 연 100만원 신규 지원',
        detail:
            '중산층 가구까지 지원이 확대되었습니다. 9구간은 연 100만원(학기당 50만원), 다자녀 가구는 첫째·둘째 135만원, 셋째 이상 200만원을 받습니다. 8구간 이하 다자녀 가구의 셋째 이상 자녀는 등록금 전액이 지원됩니다.',
        slug: 'gukga-janghakgeum',
    },
    {
        id: 'yugagi-danchuk-up',
        category: 'parenting',
        title: '육아기 근로시간 단축급여 인상',
        before: '최초 10시간분 월 상한 220만원',
        after: '최초 10시간분 월 상한 250만원',
        detail:
            '2026년 1월 1일부터 적용됩니다. 나머지 단축분의 상한도 월 150만원에서 160만원으로 올랐습니다.',
        slug: 'yugagi-geunro-danchuk',
    },
    {
        id: 'energy-voucher-unify',
        category: 'welfare',
        title: '에너지바우처 연간 통합 사용',
        before: '하절기·동절기 분리 지급',
        after: '계절 구분 없이 연간 총액 자유 사용',
        detail:
            '1인 세대 295,200원부터 4인 이상 세대 701,300원까지 지원되며, 2026년 6월 15일부터 신청해 2027년 5월 31일까지 사용할 수 있습니다.',
        slug: 'energy-voucher',
    },
    {
        id: 'wolse-sangsi',
        category: 'housing',
        title: '청년월세 지원 상시 신청 전환',
        before: '한시 사업, 정해진 모집 기간에만 신청',
        after: '연중 상시 신청, 최대 24개월 480만원',
        detail:
            '특정 모집 기간을 기다릴 필요 없이 언제든 신청할 수 있게 되었고, 지원 기간도 최대 24개월로 늘었습니다.',
        slug: 'cheongnyeon-wolse-jiwon',
    },
    {
        id: 'jugeo-imdaeryo-up',
        category: 'housing',
        title: '주거급여 기준임대료 인상',
        before: '급지·가구원수별 종전 기준',
        after: '급지·가구원수별 1.7만~3.9만원 인상',
        detail:
            '서울 1급지 1인 가구 기준 월 약 34만원까지 지원됩니다. 선정기준도 4인 가구 월 3,117,474원으로 완화되었습니다.',
        slug: 'jugeo-geubyeo',
    },
];
